/**
 * De qué país es la agencia que se está dando de alta.
 *
 * El formulario de alta dejó de preguntarlo (decisión de producto del 2026-09-07: pide nombre,
 * email y agencia, nada más), pero el dato no es cosmético: el backend lo guarda en un campo
 * NOT NULL y de ahí sale la MONEDA por defecto de la agencia (`Agency.get_default_currency`).
 * Una agencia mexicana que quedara en AR vería sus presupuestos en pesos argentinos.
 *
 * Así que se detecta y viaja oculto, sin molestar a quien se registra. La zona horaria del
 * navegador es la señal más confiable de ubicación: `navigator.language` dice en qué idioma
 * está el Chrome, no dónde está la persona (un Chrome en inglés en Buenos Aires diría US).
 * El idioma queda de segundo intento, y recién después el default histórico.
 */

// Zona horaria IANA → país (ISO 3166-1 alpha-2). Cubre los mercados donde Dashbo da de alta
// agencias; lo que caiga afuera pasa al siguiente intento. No pretende ser exhaustivo: es una
// heurística con dos redes abajo, no una fuente de verdad.
const TIMEZONE_COUNTRIES = {
    'America/Argentina/Buenos_Aires': 'AR',
    'America/Argentina/Cordoba': 'AR',
    'America/Argentina/Mendoza': 'AR',
    'America/Argentina/Salta': 'AR',
    'America/Argentina/Tucuman': 'AR',
    'America/Argentina/Jujuy': 'AR',
    'America/Argentina/San_Juan': 'AR',
    'America/Argentina/San_Luis': 'AR',
    'America/Argentina/La_Rioja': 'AR',
    'America/Argentina/Catamarca': 'AR',
    'America/Argentina/Rio_Gallegos': 'AR',
    'America/Argentina/Ushuaia': 'AR',
    'America/Buenos_Aires': 'AR',
    'America/Montevideo': 'UY',
    'America/Asuncion': 'PY',
    'America/Santiago': 'CL',
    'America/Punta_Arenas': 'CL',
    'America/La_Paz': 'BO',
    'America/Lima': 'PE',
    'America/Bogota': 'CO',
    'America/Caracas': 'VE',
    'America/Guayaquil': 'EC',
    'America/Mexico_City': 'MX',
    'America/Monterrey': 'MX',
    'America/Cancun': 'MX',
    'America/Tijuana': 'MX',
    'America/Merida': 'MX',
    'America/Chihuahua': 'MX',
    'America/Hermosillo': 'MX',
    'America/Mazatlan': 'MX',
    'America/Guatemala': 'GT',
    'America/El_Salvador': 'SV',
    'America/Tegucigalpa': 'HN',
    'America/Managua': 'NI',
    'America/Costa_Rica': 'CR',
    'America/Panama': 'PA',
    'America/Havana': 'CU',
    'America/Santo_Domingo': 'DO',
    'America/Puerto_Rico': 'PR',
    'America/Sao_Paulo': 'BR',
    'America/Bahia': 'BR',
    'America/Fortaleza': 'BR',
    'America/Recife': 'BR',
    'America/Manaus': 'BR',
    'America/Belem': 'BR',
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Denver': 'US',
    'America/Phoenix': 'US',
    'America/Los_Angeles': 'US',
    'America/Anchorage': 'US',
    'America/Toronto': 'CA',
    'America/Vancouver': 'CA',
    'America/Edmonton': 'CA',
    'America/Winnipeg': 'CA',
    'Europe/Madrid': 'ES',
    'Atlantic/Canary': 'ES',
    'Europe/Lisbon': 'PT',
    'Europe/London': 'GB',
    'Europe/Paris': 'FR',
    'Europe/Berlin': 'DE',
    'Europe/Rome': 'IT',
    'Europe/Amsterdam': 'NL',
};

// Con qué se queda cuando no se pudo detectar nada. Es el mismo país que el select del
// formulario traía preseleccionado hasta hoy, así que el peor caso repite lo de siempre.
export const DEFAULT_COUNTRY = 'AR';

function countryFromTimezone() {
    try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return TIMEZONE_COUNTRIES[timezone] || null;
    } catch (error) {
        return null;
    }
}

function countryFromLanguage() {
    try {
        // De 'es-AR' interesa el 'AR'. Un 'es' pelado o un 'es-419' (Latinoamérica, que no es
        // un país) no sirven: se descarta todo lo que no sean dos letras.
        const region = (navigator.language || '').split('-')[1];
        return /^[A-Za-z]{2}$/.test(region || '') ? region.toUpperCase() : null;
    } catch (error) {
        return null;
    }
}

/**
 * Devuelve el código ISO alpha-2 del país, siempre con un valor usable.
 * @param {string[]} [validCountries] Códigos que el backend acepta. Si se pasan, lo detectado
 *        se descarta cuando no está en la lista, para no mandar algo que rebote el alta.
 */
export function detectCountry(validCountries) {
    const isValid = (code) => !validCountries || validCountries.length === 0 || validCountries.includes(code);

    const detected = countryFromTimezone() || countryFromLanguage();
    if (detected && isValid(detected)) {
        return detected;
    }
    return DEFAULT_COUNTRY;
}
